// Generated by ts-to-zod
import type { refundApi } from 'app/src/services/welfareportal'
import { z } from 'zod'

export const subjectSchema: z.ZodType<refundApi.Subject> = z.object({
  fiscalCode: z.string(),
  firstName: z.string(),
  lastName: z.string(),
})

export const refundAttributesSchema: z.ZodType<refundApi.RefundAttributes> = z.record(z.string(), z.any())

export const emptyObjectSchema: z.ZodType<refundApi.EmptyObject> = z.object({})

export const documentInfoSchema: z.ZodType<refundApi.DocumentInfo> = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  mimeType: z.string(),
  size: z.number(),
  status: z.string(),
  scope: z.string(),
  entityId: z.string().optional(),
  dirty: z.boolean(),
  locked: z.boolean(),
  fileUrl: z.string(),
})

export const documentMetadataSchema: z.ZodType<refundApi.DocumentMetadata> = z.record(z.string(), z.any())

export const valueStringSchema: z.ZodType<refundApi.ValueString> = z.object({
  originalValue: z.string().optional(),
  value: z.string().optional(),
  confidence: z.number().optional(),
})

export const valueDateSchema: z.ZodType<refundApi.ValueDate> = z.object({
  originalValue: z.iso.datetime().optional(),
  value: z.iso.datetime().optional(),
  confidence: z.number().optional(),
})

export const valueNumberSchema: z.ZodType<refundApi.ValueNumber> = z.object({
  originalValue: z.number().optional(),
  value: z.number().optional(),
  confidence: z.number().optional(),
})

export const valueAddressSchema: z.ZodType<refundApi.ValueAddress> = z.object({
  houseNumber: valueStringSchema.optional(),
  road: valueStringSchema.optional(),
  postalCode: valueStringSchema.optional(),
  city: valueStringSchema.optional(),
  streetAddress: valueStringSchema.optional(),
  stateDistrict: valueStringSchema.optional(),
})

export const documentSchema: z.ZodType<refundApi.Document> = z.object({
  documentInfo: documentInfoSchema,
  documentInfoSignature: z.string(),
  metadata: documentMetadataSchema,
})

export const refundQuotesSchema: z.ZodType<refundApi.RefundQuotes> = z.object({
  refundable: z.number().optional(),
  deductible: z.number().optional(),
  rejected: z.number().optional(),
})

export const refundStatusValueSchema: z.ZodType<refundApi.RefundStatusValue> = z.union([
  z.literal('DRAFT'),
  z.literal('PENDING'),
  z.literal('SUBMITTED'),
  z.literal('IN_PROGRESS'),
  z.literal('REFUNDED'),
  z.literal('REJECTED'),
  z.literal('FAILED'),
  z.literal('CANCELED'),
])

export const documentAssociateRequestSchema: z.ZodType<refundApi.DocumentAssociateRequest> = z.object({
  documents: z.array(documentSchema),
})

export const errorModelSchema: z.ZodType<refundApi.ErrorModel> = z.object({
  type: z.string(),
  status: z.number(),
  title: z.string(),
  rc: z.int32().optional(),
})

export const paginationResultPagingSchema: z.ZodType<refundApi.PaginationResultPaging> = z.object({
  offset: z.int32(),
  limit: z.int32(),
  total: z.int32(),
})

export const refundStatusHookRequestSchema: z.ZodType<refundApi.RefundStatusHookRequest> = z.object({
  refundId: z.string(),
  status: z.union([z.literal('REFUNDED'), z.literal('REJECTED'), z.literal('IN_PROGRESS'), z.literal('CANCELED')]),
  statusUpdateDateTime: z.iso.datetime(),
  statusDescription: z.string(),
  quotes: z.object({
    refundable: z.number(),
    deductible: z.number(),
    rejected: z.number(),
  }),
})

export const additionalKeySchema: z.ZodType<refundApi.AdditionalKey> = z.object({
  label: z.string().optional(),
  key: z.string().optional(),
  tag: z.string().optional(),
  format: z.string().optional(),
  filter: z.string().optional(),
})

export const ruleDocumentSchema: z.ZodType<refundApi.RuleDocument> = z.object({
  name: z.string(),
  description: z.string(),
  descriptionModal: z.string().optional(),
  types: z.array(z.string()),
  extractionType: z.string().optional(),
  min: z.number().min(0),
  max: z.number().min(1),
})

export const invoiceConstraintSchema: z.ZodType<refundApi.InvoiceConstraint> = z.object({
  key: z.string(),
  operator: z.string(),
  value: z.string(),
})

export const refundStatusSchema: z.ZodType<refundApi.RefundStatus> = z.object({
  value: refundStatusValueSchema,
  message: z.string().optional(),
  updatedAt: z.iso.datetime().optional(),
})

export const invoiceDataSchema: z.ZodType<refundApi.InvoiceData> = z
  .object({
    invoiceId: valueStringSchema.optional(),
    invoiceDate: valueDateSchema.optional(),
    invoiceTotal: valueNumberSchema,
    totalTax: valueNumberSchema.optional(),
    subTotal: valueNumberSchema.optional(),
    customerAddress: valueAddressSchema.optional(),
    customerAddressRecipient: valueStringSchema.optional(),
    customerName: valueStringSchema.optional(),
    customerTaxId: valueStringSchema.optional(),
    vendorAddress: valueAddressSchema.optional(),
    vendorAddressRecipient: valueStringSchema.optional(),
    vendorName: valueStringSchema.optional(),
    vendorTaxId: valueStringSchema.optional(),
  })
  .and(z.record(z.string(), z.any()))

export const invoiceSchema: z.ZodType<refundApi.Invoice> = z.object({
  documentInfo: documentInfoSchema,
  documentInfoSignature: z.string(),
  data: invoiceDataSchema,
  dataSignature: z.string(),
  metadata: documentMetadataSchema,
})

export const refundBaseSchema: z.ZodType<refundApi.RefundBase> = z.object({
  refundId: z.string(),
  subject: subjectSchema.optional(),
  category: z.string().optional(),
  kind: z.string(),
  date: z.iso.datetime(),
  status: refundStatusSchema,
  documents: z.array(documentSchema).optional(),
  attributes: refundAttributesSchema.optional(),
  invoice: invoiceSchema.optional(),
  requestedTotal: z.number().optional(),
  quotes: refundQuotesSchema.optional(),
  isCancelable: z.boolean(),
})

export const ruleSchema: z.ZodType<refundApi.Rule> = z.object({
  name: z.string(),
  kind: z.string(),
  allowedSubjects: z.array(z.string()),
  period: z.array(z.string()),
  externalKeys: z.array(additionalKeySchema).optional(),
  documents: z.array(ruleDocumentSchema),
  termsAndConditions: z.string(),
  additionalAttributes: z
    .object({
      radio: z
        .array(
          z.object({
            title: z.string().optional(),
            attrs: z
              .array(
                z.object({
                  name: z.string().optional(),
                  text: z.string().optional(),
                }),
              )
              .optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  invoiceConstraints: z.array(invoiceConstraintSchema).optional(),
})

export const paginationResultSchema: z.ZodType<refundApi.PaginationResult> = z.object({
  paging: paginationResultPagingSchema.optional(),
})

export const refundSchema: z.ZodType<refundApi.Refund> = refundBaseSchema.and(
  z.object({
    rule: ruleSchema.optional(),
    detailUrl: z.string().optional(),
  }),
)

export const refundConfResponseSchema: z.ZodType<refundApi.RefundConfResponse> = z.object({
  rules: z.array(ruleSchema),
})

export const refundCreateRequestSchema: z.ZodType<refundApi.RefundCreateRequest> = z.object({
  kind: z.string(),
  category: z.string().optional(),
  subject: subjectSchema.optional(),
  invoice: invoiceSchema.optional(),
  documents: z.array(documentSchema).optional(),
  attributes: refundAttributesSchema.optional(),
})

export const refundConfirmRequestSchema: z.ZodType<refundApi.RefundConfirmRequest> = z.object({
  invoice: invoiceSchema.optional(),
  documents: z.array(documentSchema).optional(),
  requestedTotal: z.number().optional(),
  attributes: refundAttributesSchema.optional(),
})

export const refundConfirmResponseSchema: z.ZodType<refundApi.RefundConfirmResponse> = z.object({
  status: refundStatusSchema,
})

export const associateInvoiceSchema: z.ZodType<refundApi.AssociateInvoice> = invoiceSchema.and(
  z.object({
    requestedTotal: z.number(),
  }),
)

export const refundPaginatedSchema: z.ZodType<refundApi.RefundPaginated> = paginationResultSchema.and(
  z.object({
    items: z.array(refundSchema).optional(),
  }),
)
